import React, { useState } from "react";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import {
  buildTicketConfigurationText,
  formatTicketCurrency,
  getCommerceItems,
  getTicketAvailability,
  ticketLabelToText,
} from "../../../utils/ticketConfiguration";

const paymentTypeLabels = {
  payment: "Inline payment",
  paymentLink: "Payment link",
};

const getRuleLabels = (ticket, ruleName, ticketNames) =>
  (Array.isArray(ticket?.[ruleName]) ? ticket[ruleName] : [])
    .filter(Boolean)
    .map((fieldName) => ticketNames.get(fieldName) || fieldName);

export const TicketConfigurationSummary = ({
  eventData,
  eventSoldCount = 0,
  soldCounts = {},
}) => {
  const [copyStatus, setCopyStatus] = useState("Copy for client email");
  const tickets = getCommerceItems(eventData);
  const ticketNames = new Map(
    tickets.map((ticket, index) => [
      ticket.fieldName,
      ticketLabelToText(ticket.fieldLabel) ||
        ticket.fieldName ||
        `Ticket ${index + 1}`,
    ]),
  );
  const paymentType = paymentTypeLabels[eventData?.paymentType] || "Disabled";
  const environment = eventData?.prodPayment ? "Production" : "Test";
  const taxRate = Number(eventData?.paymentTax || 0);

  const handleCopy = async () => {
    if (!navigator.clipboard?.writeText) {
      setCopyStatus("Copy unavailable");
      return;
    }

    try {
      await navigator.clipboard.writeText(
        buildTicketConfigurationText(eventData, soldCounts, eventSoldCount),
      );
      setCopyStatus("Copied for client email");
    } catch {
      setCopyStatus("Copy failed");
    }
  };

  return (
    <section
      aria-labelledby="ticket-configuration-heading"
      className="rounded-lg border border-zinc-200 bg-zinc-100/70 p-4 dark:border-zinc-700 dark:bg-zinc-800/60"
      data-testid="ticket-configuration-summary"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2
            className="mb-1 text-lg font-semibold text-zinc-900 dark:text-zinc-100"
            id="ticket-configuration-heading"
          >
            Current ticket configuration
          </h2>
          <p className="mb-0 text-sm text-zinc-600 dark:text-zinc-300">
            Live sold counts are shown with the currently saved ticket rules.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-700 ring-1 ring-inset ring-zinc-200 dark:bg-zinc-900 dark:text-zinc-300 dark:ring-zinc-700"
            data-testid="ticket-payment-summary"
          >
            <strong>{paymentType}</strong> · {environment} · {taxRate}% tax ·{" "}
            {tickets.length} ticket {tickets.length === 1 ? "type" : "types"}
          </div>
          <button
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100 dark:bg-zinc-900 dark:text-zinc-100 dark:ring-zinc-600 dark:hover:bg-zinc-700"
            onClick={handleCopy}
            type="button"
          >
            <ClipboardDocumentIcon aria-hidden="true" className="size-5" />
            {copyStatus}
          </button>
        </div>
      </div>

      {tickets.length ? (
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {tickets.map((ticket, index) => {
            const ticketName = ticketNames.get(ticket.fieldName);
            const availability = getTicketAvailability(
              ticket,
              soldCounts[ticket.fieldName],
              eventData,
              eventSoldCount,
            );
            const showRules = getRuleLabels(
              ticket,
              "commerceItemPrerequisites",
              ticketNames,
            );
            const hideRules = getRuleLabels(
              ticket,
              "commerceItemHidePrerequisites",
              ticketNames,
            );

            return (
              <article
                className="rounded-md bg-zinc-50 p-3 text-sm ring-1 ring-inset ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-700"
                key={ticket.fieldName || index}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="mb-0 whitespace-pre-line font-semibold text-zinc-900 dark:text-zinc-100">
                      {ticketName}
                    </h3>
                    <code className="break-all text-xs text-zinc-500 dark:text-zinc-400">
                      {ticket.fieldName || "Name not configured"}
                    </code>
                  </div>
                  <span className="shrink-0 font-semibold text-zinc-900 dark:text-zinc-100">
                    {formatTicketCurrency(ticket.fieldCost)}
                  </span>
                </div>
                <dl className="mb-0 mt-3 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-zinc-700 dark:text-zinc-300">
                  <dt className="font-medium">Sold</dt>
                  <dd className="mb-0">{availability.sold}</dd>
                  <dt className="font-medium">Remaining</dt>
                  <dd className="mb-0">
                    {availability.remaining == null
                      ? availability.scope === "unlimited"
                        ? "Not limited"
                        : "Capacity not configured"
                      : availability.remaining}
                  </dd>
                  <dt className="font-medium">Visibility</dt>
                  <dd className="mb-0">
                    {showRules.length
                      ? `Shown after any: ${showRules.join(", ")}. `
                      : "Shown by default. "}
                    {hideRules.length
                      ? `Hidden after any: ${hideRules.join(", ")}.`
                      : "No hide rules."}
                  </dd>
                  <dt className="font-medium">Item check-in</dt>
                  <dd className="mb-0">
                    {ticket.fieldCheckIn ? "Enabled" : "Disabled"}
                  </dd>
                </dl>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="mb-0 mt-4 rounded-md bg-zinc-50 p-3 text-sm text-zinc-600 ring-1 ring-inset ring-zinc-200 dark:bg-zinc-900 dark:text-zinc-300 dark:ring-zinc-700">
          No Commerce Item fields are configured. Add one in Event Setup to
          create a ticket type.
        </p>
      )}
    </section>
  );
};
